import { useState, useMemo, useEffect, useCallback } from 'react';
import { Check, AlertTriangle, Link2, Search, Building2, ArrowRight, Sparkles, X, Save, Download, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { fuzzyMatch } from '@/types/room-variant-mapping';
import { useRoomMappings, useSaveRoomMappings } from '@/hooks/use-room-mappings';
import { fetchCompetitors, fetchCompetitorRoomsOnDemand, fetchCompetitorAnalysisData } from '@/api/generic';
import { useToast } from '@/hooks/use-toast';

// Import JSON data for competitor rooms (fallback)
import competitorRawData from '@/data/competitor_raw_data.json';

// Types based on actual JSON structure
export interface PropertyRoomVariant {
  variant_rel_id: number;
  variant_id: number;
  variant_name: string;
  room_type_id: number;
  room_type_name: string;
  current_price: number;
  adult: number;
  child: number;
}

export interface CompetitorRoom {
  property_name: string;
  platform: string;
  room_type_name: string;
  bed_type: string;
  price_per_night: string;
  meal_plan: string;
  room_capacity_adults: string;
  room_capacity_children: string;
  checkin: string;
}

export interface RoomMapping {
  id: string;
  yourVariantId: number;
  yourVariantName: string;
  yourRoomTypeName: string;
  competitorProperty: string;
  competitorRoomName: string;
  competitorPlatform: string;
  matchConfidence: number;
  isManualOverride: boolean;
  createdAt: string;
  bedType?: string;
  mealPlan?: string;
  roomCapacityAdults?: string;
  roomCapacityChildren?: string;
}

interface AnalysisRoomMappingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingMappings?: RoomMapping[];
  onSaveMappings: (mappings: RoomMapping[], competitorHotelName: string) => void;
  selectedDate?: string;
  propertyId: string;
  branchId: number;
  // Can still accept already fetched data if available
  apiCompetitorRooms?: CompetitorRoom[];
}

// Export helper to parse room variants from API response
export function parseRoomVariantsFromApiData(apiData: any): PropertyRoomVariant[] {
  const variants: PropertyRoomVariant[] = [];
  
  // The data structure is: response.data.data where keys are variant_rel_ids
  const data = apiData?.data;
  
  if (data && typeof data === 'object') {
    Object.entries(data).forEach(([variantRelId, dateData]: [string, any]) => {
      // Skip non-numeric keys (like org_id, property_id, cm_property_id)
      if (isNaN(parseInt(variantRelId))) {
        return;
      }
      
      // Get the first date's data
      const firstDate = Object.keys(dateData)[0];
      if (firstDate && dateData[firstDate]) {
        // Prefer METTASTAY channel, fallback to first available channel
        const channelData = dateData[firstDate]['METTASTAY'] || Object.values(dateData[firstDate])[0];
        if (channelData && channelData.variant_id) {
          variants.push({
            variant_rel_id: parseInt(variantRelId),
            variant_id: channelData.variant_id,
            variant_name: channelData.variant_name,
            room_type_id: channelData.room_type_id,
            room_type_name: channelData.room_type_name,
            current_price: channelData.current_price,
            adult: channelData.adult,
            child: channelData.child,
          });
        }
      }
    });
  }
  
  return variants;
}

// Export helper to get competitor rooms from JSON data or API
export function getCompetitorRooms(apiRooms?: CompetitorRoom[]): CompetitorRoom[] {
  const allRooms: CompetitorRoom[] = [];
  
  // First, add rooms from the local JSON file (competitor_raw_data.json)
  // if (Array.isArray(competitorRawData)) {
  //   competitorRawData.forEach((item: any) => {
  //     allRooms.push({
  //       property_name: item.property_name || '',
  //       platform: item.platform || '',
  //       room_type_name: item.room_type_name || '',
  //       bed_type: item.bed_type || '',
  //       price_per_night: item.price_per_night || '0',
  //       meal_plan: item.meal_plan || '',
  //       room_capacity_adults: item.room_capacity_adults || '2',
  //       room_capacity_children: item.room_capacity_children || '0',
  //       checkin: item.checkin || '',
  //     });
  //   });
  // }
  
  // Then add API-provided rooms if any
  if (apiRooms && apiRooms.length > 0) {
    allRooms.push(...apiRooms);
  }
  
  // Deduplicate by unique key (property + room + platform)
  const uniqueRooms = allRooms.reduce((acc: CompetitorRoom[], room) => {
    const key = `${room.property_name}-${room.room_type_name}-${room.platform}`;
    if (!acc.find(r => `${r.property_name}-${r.room_type_name}-${r.platform}` === key)) {
      acc.push(room);
    }
    return acc;
  }, []);
  
  return uniqueRooms;
}

export function AnalysisRoomMappingDialog({
  open,
  onOpenChange,
  existingMappings = [],
  onSaveMappings,
  propertyId,
  branchId,
  apiCompetitorRooms = [],
}: AnalysisRoomMappingDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompetitor, setSelectedCompetitor] = useState<string>('all');
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');
  const [mappings, setMappings] = useState<RoomMapping[]>(existingMappings);
  const [selectedYourRoom, setSelectedYourRoom] = useState<PropertyRoomVariant | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingCompetitors, setIsLoadingCompetitors] = useState(false);
  const [fetchedCompetitorRooms, setFetchedCompetitorRooms] = useState<CompetitorRoom[]>([]);
  const [isFetchingRoomsFor, setIsFetchingRoomsFor] = useState<string | null>(null);
  const [fetchedCompetitorCache, setFetchedCompetitorCache] = useState<Set<string>>(new Set());
  const [yourRoomVariants, setYourRoomVariants] = useState<PropertyRoomVariant[]>([]);
  const [isLoadingRoomVariants, setIsLoadingRoomVariants] = useState(false);
  const [activeCompetitors, setActiveCompetitors] = useState<string[]>([]);
  const { toast } = useToast();

  // Reset state when branchId changes to prevent old data from showing
  useEffect(() => {
    if (branchId > 0) {
      // Clear all cached data when branch changes
      setYourRoomVariants([]);
      setFetchedCompetitorRooms([]);
      setFetchedCompetitorCache(new Set());
      setActiveCompetitors([]);
      setSelectedYourRoom(null);
      setSearchQuery('');
      setSelectedCompetitor('all');
      setSelectedPlatform('all');
    }
  }, [branchId]);
  
  // Sync mappings when existingMappings changes
  useEffect(() => {
    setMappings(existingMappings);
  }, [existingMappings]);
  
  // Fetch room variants from API
  const fetchRoomVariantsData = useCallback(async () => {
    if (branchId === 0) return;
    
    setIsLoadingRoomVariants(true);
    try {
      const response = await fetchCompetitorAnalysisData(branchId);
      console.log('Fetched room variants data:', response);
      
      if (response.success && response.data) {
        const variants = parseRoomVariantsFromApiData(response.data);
        setYourRoomVariants(variants);
        console.log('Parsed room variants:', variants);
      } else {
        console.error('Failed to fetch room variants:', response.message);
        toast({
          title: 'Error',
          description: 'Failed to load room variants',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error fetching room variants:', error);
      toast({
        title: 'Error',
        description: 'Failed to load room variants',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingRoomVariants(false);
    }
  }, [branchId, toast]);
  
  // Fetch competitors from API
  const fetchCompetitorsData = useCallback(async () => {
    if (branchId === 0) return;
    
    setIsLoadingCompetitors(true);
    try {
      const competitorsData = await fetchCompetitors(branchId);
      console.log('Fetched competitors for room mapping:', competitorsData);
      
      // Store active competitor names for the dropdown
      const activeCompetitorNames = competitorsData
        .filter(comp => comp.is_active)
        .map(comp => comp.name);
      setActiveCompetitors(activeCompetitorNames);
      
      // Don't create default rooms - competitors will only show rooms that have been fetched on-demand
      // The fetchedCompetitorRooms state will be populated by the fetchRoomsOnDemand function
      // This ensures we only show actual room data from the API, not placeholder data
      
    } catch (error) {
      console.error('Error fetching competitors for room mapping:', error);
    } finally {
      setIsLoadingCompetitors(false);
    }
  }, [branchId]);
  
  // Fetch competitor rooms on demand for a specific competitor
  const fetchRoomsOnDemand = useCallback(async (competitorName: string, forceRefresh = false) => {
    // Check cache - skip if already fetched unless force refresh
    if (!forceRefresh && fetchedCompetitorCache.has(competitorName)) {
      toast({
        title: 'Already fetched',
        description: `Room data for ${competitorName} is already loaded`,
      });
      return;
    }
    
    setIsFetchingRoomsFor(competitorName);
    try {
      const response = await fetchCompetitorRoomsOnDemand(competitorName);
      console.log('Fetched competitor rooms on demand:', response);
      
      if (response.success && response.data) {
        let rooms: CompetitorRoom[] = [];
        
        // Handle new API response structure with room_types array
        if (response.data.room_types && Array.isArray(response.data.room_types)) {
          const propertyName = response.data.property_name || competitorName;
          
          // Transform room_types array to CompetitorRoom format
          rooms = response.data.room_types.map((roomTypeName: string) => ({
            property_name: propertyName,
            platform: 'booking', // Default platform, can be updated based on actual data
            room_type_name: roomTypeName,
            bed_type: 'Standard', // Default values since not provided in API
            price_per_night: '0',
            meal_plan: 'Room Only',
            room_capacity_adults: '2',
            room_capacity_children: '0',
            checkin: new Date().toISOString().split('T')[0],
          }));
        } 
        // Handle legacy array format if API returns it
        else if (Array.isArray(response.data)) {
          rooms = response.data.map((item: any) => ({
            property_name: item.property_name || competitorName,
            platform: item.platform || 'booking',
            room_type_name: item.room_type_name || '',
            bed_type: item.bed_type || 'Standard',
            price_per_night: item.price_per_night || '0',
            meal_plan: item.meal_plan || 'Room Only',
            room_capacity_adults: item.room_capacity_adults || '2',
            room_capacity_children: item.room_capacity_children || '0',
            checkin: item.checkin || new Date().toISOString().split('T')[0],
          }));
        }
        
        // Add fetched rooms to the existing ones
        setFetchedCompetitorRooms(prev => {
          // Remove any placeholder rooms for this competitor
          const filtered = prev.filter(r => r.property_name !== competitorName || r.room_type_name !== 'Standard Room');
          return [...filtered, ...rooms];
        });
        
        // Add to cache
        setFetchedCompetitorCache(prev => new Set([...prev, competitorName]));
        
        toast({
          title: 'Rooms fetched',
          description: `Found ${rooms.length} room types for ${competitorName}`,
        });
      } else {
        toast({
          title: 'No rooms found',
          description: response.message || 'Could not fetch competitor rooms',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error fetching competitor rooms on demand:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch competitor rooms',
        variant: 'destructive',
      });
    } finally {
      setIsFetchingRoomsFor(null);
    }
  }, [toast, fetchedCompetitorCache]);
  
  // Fetch competitors and room variants when dialog opens
  useEffect(() => {
    if (open && branchId > 0) {
      fetchCompetitorsData();
      fetchRoomVariantsData();
    }
  }, [open, branchId, fetchCompetitorsData, fetchRoomVariantsData]);
  
  // Combine all competitor room sources: JSON data + API rooms + fetched competitor placeholders
  const competitorRooms = useMemo(() => {
    const combinedApiRooms = [...fetchedCompetitorRooms, ...apiCompetitorRooms];
    return getCompetitorRooms(combinedApiRooms);
  }, [apiCompetitorRooms, fetchedCompetitorRooms]);

  // Check if a competitor has only placeholder rooms (needs on-demand fetch)
  // Also checks if competitor was already fetched (cached)
  const hasOnlyPlaceholderRooms = useCallback((competitorName: string) => {
    // If already fetched, don't show as placeholder
    if (fetchedCompetitorCache.has(competitorName)) {
      return false;
    }
    const competitorRoomsList = competitorRooms.filter(r => r.property_name === competitorName);
    return competitorRoomsList.length === 0 || 
           competitorRoomsList.every(r => r.room_type_name === 'Standard Room' && r.price_per_night === '0');
  }, [competitorRooms, fetchedCompetitorCache]);
  
  // Check if a competitor is already cached
  const isCompetitorCached = useCallback((competitorName: string) => {
    return fetchedCompetitorCache.has(competitorName);
  }, [fetchedCompetitorCache]);

  // Get unique competitor property names from both active competitors and fetched rooms
  const competitorProperties = useMemo(() => {
    const properties = new Set<string>();
    
    // Add all active competitors
    activeCompetitors.forEach(comp => properties.add(comp));
    
    // Add competitors that have fetched rooms
    competitorRooms.forEach(r => properties.add(r.property_name));
    
    return Array.from(properties).sort();
  }, [competitorRooms, activeCompetitors]);

  // Get unique platforms
  const platforms = useMemo(() => {
    const platformSet = new Set(competitorRooms.map(r => r.platform));
    return Array.from(platformSet);
  }, [competitorRooms]);

  // Filter competitor rooms
  const filteredCompetitorRooms = useMemo(() => {
    return competitorRooms.filter(r => {
      const matchesCompetitor = selectedCompetitor === 'all' || r.property_name === selectedCompetitor;
      const matchesPlatform = selectedPlatform === 'all' || r.platform === selectedPlatform;
      const matchesSearch = !searchQuery || 
        r.room_type_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.property_name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCompetitor && matchesPlatform && matchesSearch;
    });
  }, [competitorRooms, selectedCompetitor, selectedPlatform, searchQuery]);

  // Get fuzzy match suggestions for selected room
  const getSuggestionsForRoom = (variant: PropertyRoomVariant) => {
    return filteredCompetitorRooms.map(room => ({
      room,
      confidence: fuzzyMatch(variant.variant_name, room.room_type_name),
    })).sort((a, b) => b.confidence - a.confidence);
  };

  // Get all mappings for a variant (supports 1:many mapping)
  const getMappingsForVariant = (variantId: number): RoomMapping[] => {
    return mappings.filter(m => m.yourVariantId === variantId);
  };

  // Check if a specific competitor room is already mapped to this variant
  const isMapped = (variantId: number, competitorProperty: string, competitorRoomName: string, platform: string): boolean => {
    return mappings.some(m => 
      m.yourVariantId === variantId && 
      m.competitorProperty === competitorProperty && 
      m.competitorRoomName === competitorRoomName &&
      m.competitorPlatform === platform
    );
  };

  // Add a mapping (allows multiple per variant)
  const addMapping = (
    variant: PropertyRoomVariant,
    competitorRoom: CompetitorRoom,
    confidence: number
  ) => {
    // Check if this exact mapping already exists
    if (isMapped(variant.variant_id, competitorRoom.property_name, competitorRoom.room_type_name, competitorRoom.platform)) {
      return; // Already mapped
    }

    const newMapping: RoomMapping = {
      id: `mapping-${variant.variant_id}-${competitorRoom.property_name}-${competitorRoom.platform}-${Date.now()}`,
      yourVariantId: variant.variant_id,
      yourVariantName: variant.variant_name,
      yourRoomTypeName: variant.room_type_name,
      competitorProperty: competitorRoom.property_name,
      competitorRoomName: competitorRoom.room_type_name,
      competitorPlatform: competitorRoom.platform,
      matchConfidence: confidence,
      isManualOverride: true,
      createdAt: new Date().toISOString(),
      bedType: competitorRoom.bed_type || undefined,
      mealPlan: competitorRoom.meal_plan || undefined,
      roomCapacityAdults: competitorRoom.room_capacity_adults || undefined,
      roomCapacityChildren: competitorRoom.room_capacity_children || undefined
    };

    setMappings(prev => [...prev, newMapping]);
  };

  // Remove a specific mapping by id
  const removeMappingById = (mappingId: string) => {
    setMappings(prev => prev.filter(m => m.id !== mappingId));
  };

  // Remove all mappings for a variant
  const removeAllMappingsForVariant = (variantId: number) => {
    setMappings(prev => prev.filter(m => m.yourVariantId !== variantId));
  };

  // Auto-map all rooms with 70%+ confidence (adds mappings, doesn't replace existing)
  const handleAutoMap = () => {
    const newMappings: RoomMapping[] = [...mappings];
    
    yourRoomVariants.forEach(variant => {
      // Find all matches with 70%+ confidence
      const suggestions = competitorRooms.map(room => ({
        room,
        confidence: fuzzyMatch(variant.variant_name, room.room_type_name),
      })).filter(s => s.confidence >= 70);
      
      suggestions.forEach(match => {
        // Skip if this exact mapping already exists
        const exists = newMappings.some(m => 
          m.yourVariantId === variant.variant_id && 
          m.competitorProperty === match.room.property_name && 
          m.competitorRoomName === match.room.room_type_name &&
          m.competitorPlatform === match.room.platform
        );
        
        if (!exists) {
          newMappings.push({
            id: `mapping-${variant.variant_id}-${match.room.property_name}-${match.room.platform}-${Date.now()}`,
            yourVariantId: variant.variant_id,
            yourVariantName: variant.variant_name,
            yourRoomTypeName: variant.room_type_name,
            competitorProperty: match.room.property_name,
            competitorRoomName: match.room.room_type_name,
            competitorPlatform: match.room.platform,
            matchConfidence: match.confidence,
            isManualOverride: false,
            createdAt: new Date().toISOString(),
            bedType: match.room.bed_type || undefined,
            mealPlan: match.room.meal_plan || undefined,
            roomCapacityAdults: match.room.room_capacity_adults || undefined,
            roomCapacityChildren: match.room.room_capacity_children || undefined
          });
        }
      });
    });
    
    setMappings(newMappings);
  };

  // Clear all mappings
  const handleClearAll = () => {
    setMappings([]);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Transform mappings into the required format for price prediction job
      // Format: { "Your Room Variant Name": ["Competitor Room 1", "Competitor Room 2", ...] }
      const propertyMappingJson: Record<string, string[]> = {};
      
      mappings.forEach(mapping => {
        const key = mapping.yourVariantName;
        const competitorRoom = mapping.competitorRoomName;
        
        if (!propertyMappingJson[key]) {
          propertyMappingJson[key] = [];
        }
        
        // Add competitor room if not already in the array
        if (!propertyMappingJson[key].includes(competitorRoom)) {
          propertyMappingJson[key].push(competitorRoom);
        }
      });
      
      console.log('Property mapping JSON to save:', propertyMappingJson);
      
      // Get the primary competitor hotel name from mappings
      const competitorHotelName = mappings.length > 0 
        ? mappings[0].competitorProperty 
        : 'Unknown';
      
      // Call the parent save handler with both formats
      onSaveMappings(mappings, competitorHotelName);
      
      // Also save to price prediction job if available
      // This will be handled by the parent component (CompetitorsView)
      // by calling updatePricePredictionJob with property_mapping_json
      
      toast({
        title: 'Mappings saved',
        description: `Saved ${Object.keys(propertyMappingJson).length} room variant mappings`,
      });
      
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving mappings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save room mappings',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Count variants that have at least one mapping
  const mappedVariantsCount = new Set(mappings.map(m => m.yourVariantId)).size;
  const totalMappingsCount = mappings.length;
  const totalCount = yourRoomVariants.length;

  // Auto-select first room if none selected
  useEffect(() => {
    if (!selectedYourRoom && yourRoomVariants.length > 0) {
      setSelectedYourRoom(yourRoomVariants[0]);
    }
  }, [yourRoomVariants, selectedYourRoom]);

  const currentSuggestions = selectedYourRoom ? getSuggestionsForRoom(selectedYourRoom) : [];
  const currentMappings = selectedYourRoom ? getMappingsForVariant(selectedYourRoom.variant_id) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[85vh] p-0 flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <Link2 className="h-5 w-5 text-primary" />
                Room Mapping for Price Analysis
              </DialogTitle>
              <DialogDescription className="mt-1">
                Map your room variants to competitor rooms for accurate price comparison
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleAutoMap} className="gap-2">
                <Sparkles className="h-4 w-4" />
                Auto-Map (70%+)
              </Button>
              <Button variant="ghost" size="sm" onClick={handleClearAll} className="text-destructive">
                Clear All
              </Button>
              <Badge variant="secondary" className="text-sm px-3 py-1">
                {mappedVariantsCount}/{totalCount} rooms • {totalMappingsCount} mappings
              </Badge>
            </div>
          </div>
        </DialogHeader>

        {/* Side-by-side layout */}
        <div className="flex flex-1 min-h-0">
          {/* Left Panel - Your Rooms */}
          <div className="w-1/3 border-r flex flex-col">
            <div className="p-4 border-b bg-muted/30">
              <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
                <Building2 className="h-4 w-4 text-primary" />
                Your Room Variants
              </h3>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {yourRoomVariants.map((variant) => {
                  const variantMappings = getMappingsForVariant(variant.variant_id);
                  const mappingCount = variantMappings.length;
                  const isSelected = selectedYourRoom?.variant_id === variant.variant_id;
                  
                  return (
                    <button
                      key={variant.variant_rel_id}
                      onClick={() => setSelectedYourRoom(variant)}
                      className={cn(
                        "w-full text-left p-3 rounded-lg transition-all",
                        isSelected 
                          ? "bg-primary text-primary-foreground" 
                          : "hover:bg-muted",
                        mappingCount > 0 && !isSelected && "border-l-4 border-green-500"
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className={cn(
                            "font-medium text-sm truncate",
                            isSelected ? "text-primary-foreground" : "text-foreground"
                          )}>
                            {variant.room_type_name}
                          </p>
                          <p className={cn(
                            "text-xs truncate mt-0.5",
                            isSelected ? "text-primary-foreground/80" : "text-muted-foreground"
                          )}>
                            {variant.adult}A {variant.child}C
                          </p>
                        </div>
                        {mappingCount > 0 && (
                          <Badge 
                            variant="outline" 
                            className={cn(
                              "text-[10px] px-1.5 py-0 flex-shrink-0",
                              isSelected ? "border-primary-foreground/50 text-primary-foreground" : "border-green-500 text-green-600"
                            )}
                          >
                            {mappingCount}
                          </Badge>
                        )}
                      </div>
                      <p className={cn(
                        "text-xs mt-1",
                        isSelected ? "text-primary-foreground/70" : "text-muted-foreground"
                      )}>
                        ₹{variant.current_price.toLocaleString()}
                      </p>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          {/* Middle Panel - Mapping Details */}
          <div className="w-1/3 border-r flex flex-col bg-muted/10">
            <div className="p-4 border-b bg-muted/30">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <ArrowRight className="h-4 w-4 text-primary" />
                Mapping
              </h3>
            </div>
            <div className="flex-1 p-4">
              {selectedYourRoom ? (
                <div className="space-y-4">
                  {/* Selected Room Info */}
                  <div className="p-3 rounded-lg bg-card border">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-sm">{selectedYourRoom.room_type_name}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {selectedYourRoom.variant_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {selectedYourRoom.adult} Adults, {selectedYourRoom.child} Child • ₹{selectedYourRoom.current_price.toLocaleString()}
                        </p>
                      </div>
                      {currentMappings.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs text-destructive hover:bg-destructive/10"
                          onClick={() => removeAllMappingsForVariant(selectedYourRoom.variant_id)}
                        >
                          Clear All
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Current Mappings (multiple) */}
                  {currentMappings.length > 0 ? (
                    <ScrollArea className="max-h-[200px]">
                      <div className="space-y-2">
                        <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                          Mapped to {currentMappings.length} competitor room{currentMappings.length > 1 ? 's' : ''}:
                        </p>
                        {currentMappings.map((mapping) => (
                          <div 
                            key={mapping.id} 
                            className="p-2 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-xs truncate">{mapping.competitorRoomName}</p>
                                <p className="text-xs text-muted-foreground truncate mt-0.5">
                                  {mapping.competitorProperty}
                                </p>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <Badge variant="outline" className="text-[9px] px-1 py-0">
                                    {mapping.competitorPlatform}
                                  </Badge>
                                  <span className="text-[10px] text-muted-foreground">
                                    {mapping.matchConfidence}% match
                                  </span>
                                </div>
                                {mapping.bedType && (
                                  <div className="mt-1 flex items-center gap-1">
                                    <span className="text-[10px] font-medium">Room:</span>
                                    <span className="text-[10px] text-muted-foreground">{mapping.bedType}</span>
                                  </div>
                                )}
                                {mapping.mealPlan && (
                                  <div className="flex items-center gap-1">
                                    <span className="text-[10px] font-medium">Meals:</span>
                                    <span className="text-[10px] text-muted-foreground">{mapping.mealPlan}</span>
                                  </div>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 text-destructive hover:bg-destructive/10 flex-shrink-0"
                                onClick={() => removeMappingById(mapping.id)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  ) : (
                    <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        <p className="text-sm text-amber-700 dark:text-amber-400">Not mapped</p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Select competitor rooms from the right panel to add mappings
                      </p>
                    </div>
                  )}

                  {/* Best suggestions for unmapped */}
                  {currentSuggestions.filter(s => 
                    s.confidence >= 50 && 
                    !isMapped(selectedYourRoom.variant_id, s.room.property_name, s.room.room_type_name, s.room.platform)
                  ).slice(0, 2).length > 0 && (
                    <div className="p-3 rounded-lg border border-dashed space-y-2">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Sparkles className="h-3 w-3" /> Suggested matches:
                      </p>
                      {currentSuggestions.filter(s => 
                        s.confidence >= 50 && 
                        !isMapped(selectedYourRoom.variant_id, s.room.property_name, s.room.room_type_name, s.room.platform)
                      ).slice(0, 2).map((suggestion, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-xs truncate">{suggestion.room.room_type_name}</p>
                            <p className="text-[10px] text-muted-foreground">
                              {suggestion.room.property_name} • {suggestion.confidence}%
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-xs"
                            onClick={() => addMapping(selectedYourRoom, suggestion.room, suggestion.confidence)}
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Add
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                  Select a room from the left panel
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Competitor Rooms */}
          <div className="w-1/3 flex flex-col">
            <div className="p-4 border-b bg-muted/30 space-y-3">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Building2 className="h-4 w-4 text-orange-500" />
                Competitor Rooms
              </h3>
              <div className="flex gap-2">
                <Select value={selectedCompetitor} onValueChange={setSelectedCompetitor}>
                  <SelectTrigger className="h-8 text-xs flex-1">
                    <SelectValue placeholder="All Properties" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Properties</SelectItem>
                    {competitorProperties.map(prop => (
                      <SelectItem key={prop} value={prop} className="text-xs">
                        {prop.length > 25 ? prop.substring(0, 25) + '...' : prop}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Fetch/Refresh button for selected competitor */}
                {selectedCompetitor !== 'all' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className={cn(
                      "h-8 px-2",
                      isCompetitorCached(selectedCompetitor) && "border-green-500/50"
                    )}
                    onClick={() => fetchRoomsOnDemand(selectedCompetitor, isCompetitorCached(selectedCompetitor))}
                    disabled={isFetchingRoomsFor === selectedCompetitor}
                    title={isCompetitorCached(selectedCompetitor) ? "Refresh rooms (already cached)" : "Fetch rooms for this competitor"}
                  >
                    {isFetchingRoomsFor === selectedCompetitor ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : isCompetitorCached(selectedCompetitor) ? (
                      <RefreshCw className="h-3.5 w-3.5 text-green-600" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                  <SelectTrigger className="h-8 text-xs w-[100px]">
                    <SelectValue placeholder="Platform" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {platforms.map(plat => (
                      <SelectItem key={plat} value={plat} className="text-xs">
                        {plat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search rooms..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-8 text-xs"
                />
              </div>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {/* Show fetch button if a specific competitor is selected and has only placeholder rooms */}
                {selectedCompetitor !== 'all' && hasOnlyPlaceholderRooms(selectedCompetitor) && (
                  <div className="p-4 mb-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                        Room data not available
                      </p>
                    </div>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
                      Click to fetch room details for this competitor
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-2 border-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900"
                      onClick={() => fetchRoomsOnDemand(selectedCompetitor)}
                      disabled={isFetchingRoomsFor === selectedCompetitor}
                    >
                      {isFetchingRoomsFor === selectedCompetitor ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Fetching...
                        </>
                      ) : (
                        <>
                          <Download className="h-3 w-3" />
                          Fetch Rooms
                        </>
                      )}
                    </Button>
                  </div>
                )}
                
                {currentSuggestions.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground text-sm">
                    {selectedCompetitor !== 'all' && hasOnlyPlaceholderRooms(selectedCompetitor) 
                      ? 'Fetch room data to see available rooms'
                      : 'No competitor rooms found'}
                  </div>
                ) : (
                  currentSuggestions.map((suggestion, idx) => {
                    const isAlreadyMapped = selectedYourRoom ? isMapped(
                      selectedYourRoom.variant_id, 
                      suggestion.room.property_name, 
                      suggestion.room.room_type_name,
                      suggestion.room.platform
                    ) : false;
                    
                    return (
                      <button
                        key={`${suggestion.room.property_name}-${suggestion.room.room_type_name}-${suggestion.room.platform}-${idx}`}
                        onClick={() => {
                          if (selectedYourRoom) {
                            if (isAlreadyMapped) {
                              // Find and remove the mapping
                              const mappingToRemove = mappings.find(m => 
                                m.yourVariantId === selectedYourRoom.variant_id &&
                                m.competitorProperty === suggestion.room.property_name &&
                                m.competitorRoomName === suggestion.room.room_type_name &&
                                m.competitorPlatform === suggestion.room.platform
                              );
                              if (mappingToRemove) {
                                removeMappingById(mappingToRemove.id);
                              }
                            } else {
                              addMapping(selectedYourRoom, suggestion.room, suggestion.confidence);
                            }
                          }
                        }}
                        disabled={!selectedYourRoom}
                        className={cn(
                          "w-full text-left p-3 rounded-lg transition-all",
                          isAlreadyMapped 
                            ? "bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700" 
                            : "hover:bg-muted border border-transparent",
                          !selectedYourRoom && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm truncate">
                                {suggestion.room.room_type_name}
                              </p>
                              {isAlreadyMapped && (
                                <Check className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground truncate mt-0.5">
                              {suggestion.room.property_name}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                {suggestion.room.platform}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                ₹{parseInt(suggestion.room.price_per_night).toLocaleString()}
                              </span>
                            </div>
                            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                              {suggestion.room.bed_type && (
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] font-medium">Room:</span>
                                  <span className="text-[10px] text-muted-foreground">{suggestion.room.bed_type}</span>
                                </div>
                              )}
                              {suggestion.room.meal_plan && (
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] font-medium">Meals:</span>
                                  <span className="text-[10px] text-muted-foreground">{suggestion.room.meal_plan}</span>
                                </div>
                              )}
                              {suggestion.room.room_capacity_adults && suggestion.room.room_capacity_children && (
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] font-medium">Capacity:</span>
                                  <span className="text-[10px] text-muted-foreground">
                                    {suggestion.room.room_capacity_adults}A, {suggestion.room.room_capacity_children}C
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <Badge 
                            variant="outline" 
                            className={cn(
                              'text-[10px] px-1.5 py-0 flex-shrink-0',
                              suggestion.confidence >= 70 && 'bg-green-100 text-green-700 border-green-300',
                              suggestion.confidence >= 50 && suggestion.confidence < 70 && 'bg-amber-100 text-amber-700 border-amber-300',
                              suggestion.confidence < 50 && 'bg-muted text-muted-foreground'
                            )}
                          >
                            {suggestion.confidence}%
                          </Badge>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <div className="flex items-center gap-2 mr-auto text-sm text-muted-foreground">
            <span>{yourRoomVariants.length} your rooms</span>
            <span>•</span>
            <span>{competitorProperties.length} competitors</span>
            <span>•</span>
            <span>{filteredCompetitorRooms.length} competitor rooms</span>
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving} className="gap-2">
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Mappings ({totalMappingsCount})
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
